import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { locale } from 'helpers/intl-helper';
import { translate } from 'translations/i18n';

import {
  fetchProjectTags,
  fetchTags,
} from 'components/assetFeed/v1api/fetchHelpers';

import { InputCheckbox } from '@companycam/slab-web';
import FilterButtonWithPopover from './shared/FilterButtonWithPopover';
import SearchInput from './shared/SearchInput';
import EmptyState from './shared/EmptyState';
import {
  CheckboxOptionsContainer,
  CheckboxOption,
} from './shared/styledComponents';

import { selectCompanyId } from 'selectors/companySelectors';
import { type Tag } from 'data/v1/data-contracts';
import { type DataTestIds } from './types';

const tagDisplayValue = (tag: Tag) => tag.display_value || tag.value || '';
const sortByDisplayValue = (tags: Tag[]) =>
  tags.sort((a, b) =>
    tagDisplayValue(a).localeCompare(tagDisplayValue(b), locale, {
      numeric: true,
    }),
  );

type TagFilterProps = {
  projectId: number;
  activeFilterTags: Tag[];
  onChange: (arg: { tags: Tag[] }) => void;
  onlyProjectTags: boolean;
  dataTestIds?: DataTestIds;
};

const TagFilter = ({
  projectId,
  activeFilterTags,
  onChange,
  onlyProjectTags,
  dataTestIds,
}: TagFilterProps) => {
  const [tags, setTags] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const companyId = useSelector(selectCompanyId);

  const tagFilterActive = activeFilterTags.length;
  const selectedTagIds = activeFilterTags.map(({ node_id }) => node_id);

  const [queryTerm, setQueryTerm] = useState('');
  const filteredTags = tags.filter((tag) =>
    tagDisplayValue(tag).toLowerCase().includes(queryTerm.toLowerCase()),
  );

  useEffect(() => {
    if (isOpen) {
      const fetchFunction = onlyProjectTags ? fetchProjectTags : fetchTags;
      const id = onlyProjectTags ? projectId : companyId;

      fetchFunction(id).then((options) => {
        setTags(options);
        setIsLoading(false);
      });
    }
  }, [projectId, isOpen, onlyProjectTags, companyId]);

  const handleToggle = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  const addOrRemove = (tag: Tag) =>
    selectedTagIds.includes(tag.node_id)
      ? activeFilterTags.filter(({ node_id }) => node_id !== tag.node_id)
      : [...activeFilterTags, tag];

  const handleChange = (tag: Tag) => {
    const nextFilterTags = addOrRemove(tag);
    onChange({ tags: nextFilterTags });
  };

  type TagQueryTerm = {
    target: {
      value: string;
    };
  };
  const handleQueryChange = ({ target: { value } }: TagQueryTerm) =>
    setQueryTerm(value);

  const handleClear = () => {
    onChange({ tags: [] });
    setIsOpen(false);
    setQueryTerm('');
  };

  const emptyStateMessage = isLoading
    ? translate('common.loading')
    : translate('filters.noResultsFound', {
        filterType: translate('common.tags'),
      });

  return (
    <FilterButtonWithPopover
      id="filter-row-tags-popover"
      isOpen={isOpen}
      onOpen={handleToggle}
      isSelected={!!tagFilterActive}
      selectedCount={tagFilterActive}
      title={translate('common.tags')}
      onClearClick={handleClear}
      data-testid={dataTestIds.filterButton}
      clearButtonDataTestId={dataTestIds.clearButton}
    >
      <>
        {filteredTags.length || queryTerm !== '' ? (
          <SearchInput
            id="filter-row-tags-input"
            label={translate('filters.tagQueryTerm')}
            placeholder={translate('common.search')}
            value={queryTerm}
            onChange={handleQueryChange}
            data-testid={dataTestIds.searchInput}
          />
        ) : null}

        {!filteredTags.length ? (
          <EmptyState message={emptyStateMessage} />
        ) : null}

        <CheckboxOptionsContainer>
          {sortByDisplayValue(filteredTags).map((option) => {
            const selected = selectedTagIds.includes(option.node_id);
            return (
              <CheckboxOption key={option.node_id}>
                <InputCheckbox
                  id={`tags-${option.node_id}`}
                  label={option.display_value}
                  value={option.value}
                  onChange={() => handleChange(option)}
                  checked={selected}
                />
              </CheckboxOption>
            );
          })}
        </CheckboxOptionsContainer>
      </>
    </FilterButtonWithPopover>
  );
};

export default TagFilter;
